import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { InputFieldComponent } from 'src/app/components/form/input-field/input-field.component';
import { LoaderComponent } from 'src/app/components/widgets/loader/loader.component';
import { Regulator, RegulatorCheck, RegulatorPayload } from 'src/app/models/entities/regulator';
import { AccessControlService } from 'src/app/services/access-control.service';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { RegulatorsService } from 'src/app/services/api/regulators.service';
import { FormService } from 'src/app/services/form.service';
import { MainLayoutService } from 'src/app/services/main-layout.service';
import { RouteService } from 'src/app/services/route.service';
import { apiErrorMessage, isValidationError } from '../regulators-ui.utils';

@Component({
  selector: 'app-regulator-edit-page',
  imports: [ReactiveFormsModule, InputFieldComponent, LoaderComponent],
  templateUrl: './regulator-edit-page.component.html',
  styleUrl: './regulator-edit-page.component.scss',
})
export class RegulatorEditPageComponent implements OnInit, OnDestroy {

  readonly regulatorId: number | null;

  form = new FormGroup({
    code: new FormControl<string>('', { nonNullable: true, validators: Validators.required }),
    name: new FormControl<string>('', { nonNullable: true, validators: Validators.required }),
    externalCode: new FormControl<string>('', { nonNullable: true }),
  });

  regulator: Regulator | null = null;
  checks: RegulatorCheck[] = [];
  isLoading = false;
  isLoadingChecks = false;
  isSyncingChecks = false;
  canModify = false;

  private subscriptions = new Subscription();

  constructor(
    private regulatorsService: RegulatorsService,
    private routeService: RouteService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService,
    private alertNotificationService: AlertNotificationService,
    private formService: FormService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    const regulatorId = Number(this.route.snapshot.paramMap.get('regulatorId'));
    this.regulatorId = regulatorId || null;
  }

  get isUpdate(): boolean {
    return this.regulatorId !== null;
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.mainLayoutService.activeProfile$.subscribe(profileCode => {
        this.canModify = this.accessControlService.hasFeatureActionAccess(profileCode, 'REGULATORS_LIST', 'modify');
      })
    );

    if (this.regulatorId) {
      this.loadRegulator(this.regulatorId);
      this.loadChecks(this.regulatorId);
    } else {
      this.setBreadcrumbs(null);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.mainLayoutService.setBreadcrumbs(null);
  }

  control(name: 'code' | 'name' | 'externalCode'): FormControl {
    return this.form.get(name) as FormControl;
  }

  goBack(): void {
    this.routeService.regulatorsIndex();
  }

  submitForm(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }

    const value = this.form.getRawValue();
    const payload: RegulatorPayload = {
      code: value.code.trim(),
      name: value.name.trim(),
      externalCode: value.externalCode.trim() || null,
    };

    this.formService.resetErrors(this.form);
    this.isLoading = true;

    const request$ = this.regulatorId
      ? this.regulatorsService.update(this.regulatorId, payload)
      : this.regulatorsService.create(payload);

    request$.subscribe({
      next: (response: any) => {
        this.isLoading = false;
        this.alertNotificationService.success('Regulator saved successfully');
        const saved: Regulator | undefined = response?.data?.regulator;
        if (this.regulatorId) {
          this.applyRegulator(saved ?? { ...this.regulator!, ...payload });
        } else if (saved?.regulatorId) {
          this.routeService.regulatorUpdate(saved.regulatorId);
        } else {
          this.goBack();
        }
      },
      error: (error: any) => {
        this.isLoading = false;
        if (isValidationError(error)) {
          this.formService.setValidationErrors(this.form, error.error.error.validationErrors);
        }
        this.alertNotificationService.error(apiErrorMessage(error, 'Error saving regulator'));
      }
    });
  }

  syncChecks(): void {
    if (!this.regulatorId || this.isSyncingChecks) {
      return;
    }

    this.isSyncingChecks = true;
    this.regulatorsService.syncChecks(this.regulatorId).subscribe({
      next: (response: any) => {
        this.isSyncingChecks = false;
        this.checks = this.sortChecks(response?.data?.checks ?? []);
        this.alertNotificationService.success('Rules synchronized successfully');
      },
      error: (error: any) => {
        this.isSyncingChecks = false;
        this.alertNotificationService.error(apiErrorMessage(error, 'Error synchronizing rules'));
      }
    });
  }

  private loadRegulator(regulatorId: number): void {
    this.isLoading = true;
    this.regulatorsService.detail(regulatorId).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        this.applyRegulator(response?.data?.regulator);
      },
      error: () => {
        this.isLoading = false;
        this.router.navigate(['/error/404']);
      }
    });
  }

  private loadChecks(regulatorId: number): void {
    this.isLoadingChecks = true;
    this.regulatorsService.listChecks(regulatorId).subscribe({
      next: (response: any) => {
        this.isLoadingChecks = false;
        this.checks = this.sortChecks(response?.data?.checks ?? []);
      },
      error: () => {
        this.isLoadingChecks = false;
        this.checks = [];
      }
    });
  }

  private applyRegulator(regulator: Regulator | null | undefined): void {
    if (!regulator) {
      return;
    }
    this.regulator = regulator;
    this.form.patchValue({
      code: regulator.code ?? '',
      name: regulator.name ?? '',
      externalCode: regulator.externalCode ?? '',
    });
    this.setBreadcrumbs(regulator);
  }

  private setBreadcrumbs(regulator: Regulator | null): void {
    this.mainLayoutService.setBreadcrumbs(regulator
      ? [
          { label: 'Regulators', routerLink: '/regulators' },
          { label: regulator.code },
          { label: 'Edit' },
        ]
      : [
          { label: 'Regulators', routerLink: '/regulators' },
          { label: 'Create' },
        ]);
  }

  private sortChecks(checks: RegulatorCheck[]): RegulatorCheck[] {
    return [...checks].sort((a, b) => (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER));
  }
}
